import { useState } from 'react'
import { GenerationType } from 'components/BlazeCommandLine/api/createDocGenerationRequest'
import { MultiPostPrompt } from './components/MultiPostPrompt/MultiPostPrompt'
import { PostTypeSelect } from './components/PostTypeSelect/PostTypeSelect'
import { MultiPostDetails } from './components/MultiPostDetails/MultiPostDetails'
import { PostTimes } from './components/PostTimes/PostTimes'
import { PostPreviewScreen } from './components/PostPreviewScreen/PostPreviewScreen'
import { observer } from 'mobx-react-lite'
import { MultiStepMobileWizard } from 'components/Wizards/MobileWizards/MultiStepMobileWizard/MultiStepMobileWizard'
import { SubmitStepProps } from 'components/Wizards/MobileWizards/MultiStepMobileWizard/Types'
import { PlanningValues, FlowProps } from 'components/Wizards/MobileWizards/Types'
import { MediaType } from 'components/Wizards/schemas/shared/mediaType'
import {
  MobileContentPlanningProvider,
  useMobileContentPlanningContext,
} from './components/ContentPlanningContext/ContentPlanningContext'
import { getAspectRatioForGenerationType, getGenerationTypesForMediaType } from './utils'

type Values = Pick<PlanningValues, 'project' | 'topic' | 'brand_kit_id' | 'tone' | 'caption_length' | 'count'> & {
  include_images?: boolean
  is_custom_schedule?: boolean
  post_time?: string
  custom_schedule_data?: {
    selectedDays: string[]
    dayTimeSelections: Record<string, any>
  }
}

interface FlowIProps extends FlowProps<GenerationType> {
  mediaType: MediaType
  setMediaType: (mediaType: MediaType) => void
}

const FlowI = observer(({ schema, mediaType, setMediaType, initialValues, ...props }: FlowIProps) => {
  const { generateCampaign } = useMobileContentPlanningContext()
  const { width, height } = getAspectRatioForGenerationType(schema.meta.channel)
  const defaultValues = {
    ...initialValues,
    width,
    height,
    include_images: mediaType === MediaType.DocumentWithImages,
  }

  const handleSubmitStep = async ({
    currentStep,
    totalSteps,
    values,
    advance,
    setCurrentStep,
  }: SubmitStepProps<Values>) => {
    if (currentStep === totalSteps - 1) {
      generateCampaign(values)
      return
    }
    advance()
  }

  return (
    <MultiStepMobileWizard<Values>
      schema={schema}
      onSubmitStep={handleSubmitStep}
      initialValues={defaultValues}
      {...props}
    >
      <MultiPostPrompt />
      <PostTypeSelect setMediaType={setMediaType} />
      <MultiPostDetails />
      <PostTimes />
      <PostPreviewScreen />
    </MultiStepMobileWizard>
  )
})

const FlowIContainer = (props: FlowProps<GenerationType>) => {
  const channel = props.schema.meta.channel
  const [mediaType, setMediaType] = useState(props.initialValues?.media_type || MediaType.DocumentWithImages)
  const { captionsGenerationType, generationType } = getGenerationTypesForMediaType(mediaType, channel)

  return (
    <MobileContentPlanningProvider
      captionsGenerationType={captionsGenerationType}
      generationType={generationType}
      mediaType={mediaType}
      initialValues={props.initialValues}
    >
      <FlowI {...props} mediaType={mediaType} setMediaType={setMediaType} />
    </MobileContentPlanningProvider>
  )
}

export { FlowIContainer as FlowI }
